import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button';
import { Input } from '../common/Input';
import { FaEnvelope } from 'react-icons/fa';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1rem;
`;

const StyledInput = styled(Input)`
  padding-left: 2.5rem;
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.div`
  color: ${({ theme }) => theme.colors.success};
  font-size: 0.9rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: ${({ theme }) => `${theme.colors.success}10`};
  border-radius: 4px;
  text-align: center;
`;

const Description = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: -0.5rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

interface ForgotPasswordFormProps {
  onSwitchToLogin: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onSwitchToLogin }) => {
  const { resetPassword, error, loading, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ email?: string }>({});
  const [resetSuccess, setResetSuccess] = useState(false);

  const validateForm = (): boolean => {
    const errors: { email?: string } = {};
    
    if (!email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'L\'email est invalide';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setResetSuccess(false);
    
    if (validateForm()) {
      try {
        await resetPassword(email);
        setResetSuccess(true);
        setEmail('');
      } catch (err) {
        // L'erreur est déjà gérée par le contexte d'authentification
      }
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      <h2>Mot de passe oublié</h2>
      <Description>
        Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
      </Description>
      
      <FormGroup>
        <Label htmlFor="email">Email</Label>
        <InputWrapper>
          <IconWrapper>
            <FaEnvelope />
          </IconWrapper>
          <StyledInput
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Votre adresse email"
            $hasError={!!validationErrors.email}
          />
        </InputWrapper>
        {validationErrors.email && <ErrorMessage>{validationErrors.email}</ErrorMessage>}
      </FormGroup>
      
      {error && <ErrorMessage>{error.message}</ErrorMessage>}
      
      {resetSuccess && (
        <SuccessMessage>
          Un email de réinitialisation a été envoyé à votre adresse email.
        </SuccessMessage>
      )}
      
      <ButtonsContainer>
        <Button type="submit" $variant="primary" disabled={loading}>
          {loading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
        </Button>
        <Button type="button" $variant="text" onClick={onSwitchToLogin}>
          Retour à la connexion
        </Button>
      </ButtonsContainer>
    </FormContainer>
  );
};

export default ForgotPasswordForm;

